import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class Api400SigelService {

  private readonly baseUrl: string;
  private readonly credentials: { email: string; password: string };
  private bearerToken: string | null = null;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.api400.ruta;
    this.credentials = {
      email: environment.api400.email,
      password: environment.api400.password,
    };
  }

  /**
   * POST api/acceso
   * Obtiene el bearer token usando las credenciales del environment.
   * Almacena el token internamente para reutilizarlo.
   */
  getToken(): Observable<any> {
    const url = `${this.baseUrl}acceso`;
    return this.http.post<any>(url, this.credentials).pipe(
      tap((response: any) => {
        // Guarda el token según la estructura que devuelva la API
        // Ajusta 'token' o 'access_token' según la respuesta real
        this.bearerToken = response?.token ?? response?.access_token ?? null;
      })
    );
  }

  /**
   * GET api/pagina-campania
   * Requiere bearer token. Si no se tiene uno en caché, lo solicita primero.
   */
  getPaginaCampania(): Observable<any> {
    if (this.bearerToken) {
      return this.fetchPaginaCampania(this.bearerToken);
    }
    // No hay token aún → autenticar primero y luego llamar al endpoint
    return this.getToken().pipe(
      switchMap(() => this.fetchPaginaCampania(this.bearerToken!))
    );
  }

  /**
   * GET api/pagina-campania/{id}
   * Obtiene un único registro de campaña por su ID.
   * Si no hay token en caché, autentica primero.
   */
  getPaginaCampaniaById(id: number | string): Observable<any> {
    if (this.bearerToken) {
      return this.fetchPaginaCampaniaById(id, this.bearerToken);
    }
    return this.getToken().pipe(
      switchMap(() => this.fetchPaginaCampaniaById(id, this.bearerToken!))
    );
  }

  /**
   * Llamada interna a GET api/pagina-campania con el token provisto.
   */
  private fetchPaginaCampania(token: string): Observable<any> {
    const url = `${this.baseUrl}pagina-campania`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.get<any>(url, { headers });
  }

  /**
   * Llamada interna a GET api/pagina-campania/{id} con el token provisto.
   */
  private fetchPaginaCampaniaById(id: number | string, token: string): Observable<any> {
    const url = `${this.baseUrl}pagina-campania/${id}`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.get<any>(url, { headers });
  }

  /**
   * Limpia el token almacenado (útil para forzar re-autenticación).
   */
  clearToken(): void {
    this.bearerToken = null;
  }
}
